// @ts-check
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { Link } from "react-router-dom";
import { HiSearch } from "react-icons/hi";
import { LuExternalLink, LuTriangleAlert } from "react-icons/lu";

import {
  TestableCard,
  TestableTextInput,
  TestableTable,
  TestableTableHead,
  TestableTableRow,
  TestableTableHeaderCell,
  TestableTableBody,
  TestableTableCell,
  TestableText,
	TestableSelect,
	TestableSelectItem,
} from "@components/general/TestableComponents";
import FilterPopover from "@components/cards/shared/filters/FilterPopover";
import ActiveFilterChips from "@components/cards/shared/filters/ActiveFilterChips";
import { matchesFilters, countSelected } from "@components/cards/shared/filters/filterUtils";
import RagBadge from "../shared/RagBadge";
import { buildFlowFilterGroups } from "./businessViewFilters";
import { COUNT_CLASS, formatCurrency, getRagRank, buildFlowDetailPath } from "../shared/businessViewConfig";
import StyledTooltip from "@components/general/StyledTooltip";

/**
 * The flows table with a toolbar (risk sort, filter, search).
 *
 * Each row links to the flow's detail view. The link is addressed by project as
 * well as flow because that is how the DashboardAPI detail operation is
 * addressed — the project cannot be inferred from the flow.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').FlowRow[]} props.flows
 * @param {string} [props.projectId]
 * @returns {JSX.Element}
 */
const BusinessViewFlowsTable = ({ flows = [], projectId = "" }) => {
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("high-risk"); // 'high-risk' = riskiest first
  const [selection, setSelection] = useState(
    /** @type {import('@components/cards/shared/filters/filterUtils').FilterSelection} */ ({})
  );

  /*
    Rebuilt from the flows because the category options are read off them — switching
    project has to re-offer that project's categories, not the previous one's.
  */
  const filterGroups = useMemo(() => buildFlowFilterGroups(flows), [flows]);

  const visibleFlows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = flows.filter((flow) => matchesFilters(flow, filterGroups, selection));
    const filtered = term ? matching.filter((f) => (f.name || "").toLowerCase().includes(term)) : matching;

    filtered.sort((a, b) => {
      const rankDelta = getRagRank(a.status) - getRagRank(b.status);
      let primary;

      switch (sortDir) {
        case "high-risk":
          primary = rankDelta;
          break;
        case "low-risk":
          primary = -rankDelta;
          break;
        case "flow-asc":
          primary = (a.name || "").localeCompare(b.name || "");
          break;
        case "flow-desc":
          primary = (b.name || "").localeCompare(a.name || "");
          break;
        case "failed-high":
          primary = (b.failedTests ?? 0) - (a.failedTests ?? 0);
          break;
        default:
          primary = rankDelta;
      }

      if (primary !== 0) return primary; // Tie-break on risk status first, then cost at risk.

      // Tie-break on cost at risk (higher cost first when sorting high -> low).
      return sortDir === "high-risk" ? b.costAtRisk - a.costAtRisk : a.costAtRisk - b.costAtRisk;
    });

    return filtered;
  }, [flows, search, sortDir, filterGroups, selection]);

  return (
		<>
    <TestableCard dataTestId="bv-flows-table-card" className="p-5 shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="w-full md:w-72 md:shrink-0">
          <TestableSelect
            className="mt-Select-button"
            dataTestId="bv-flows-sort"
            value={sortDir}
            onValueChange={(value) => value && setSortDir(value)}
            aria-label="Sort by risk"
          >
            <TestableSelectItem dataTestId="bv-flows-sort-high-risk" className="mt-Select-option" value="high-risk">
              Risk High &rarr; Low
            </TestableSelectItem>
            <TestableSelectItem dataTestId="bv-flows-sort-low-risk" className="mt-Select-option" value="low-risk">
              Risk Low &rarr; High
            </TestableSelectItem>
            <TestableSelectItem dataTestId="bv-flows-sort-flow-asc" className="mt-Select-option" value="flow-asc">
              Flow name: A &rarr; Z
            </TestableSelectItem>
            <TestableSelectItem dataTestId="bv-flows-sort-flow-desc" className="mt-Select-option" value="flow-desc">
              Flow name: Z &rarr; A
            </TestableSelectItem>
            <TestableSelectItem dataTestId="bv-flows-sort-failed-high" className="mt-Select-option" value="failed-high">
              Failed tests: High &rarr; Low
            </TestableSelectItem>
          </TestableSelect>
        </div>

        <div className="flex w-full items-center gap-3 md:flex-1">
          <FilterPopover
            dataTestId="bv-flows-filter"
            groups={filterGroups}
            selection={selection}
            onChange={setSelection}
          />
          <div className="flex-1">
            <TestableTextInput dataTestId="bv-flows-search" icon={HiSearch} placeholder="Search flows..." value={search} onValueChange={setSearch} />
          </div>
        </div>
      </div>

			{/*
				The chips are the only place the applied filters are spelled out — the button
				shows a count, not what is in it — so they sit directly under the toolbar that
				set them, above the table they act on.
			*/}
			<div className="mt-3">
				<ActiveFilterChips
					dataTestId="bv-flows-active-filters"
					groups={filterGroups}
					selection={selection}
					onChange={setSelection}
				/>
			</div>
		</TestableCard>

      {/*
        Fixed layout with declared widths so cells wrap in their column: a long flow name
        or row of categories would otherwise widen the table past the card into a
        scrollbar. Percentages cover the visible columns only, so they exceed 100% once the
        md-only one is hidden - the browser scales them back.

        A style rather than a class because Tremor puts className on the scroll container
        that wraps the table, not on the <table> itself.
      */}
			<TestableCard dataTestId="bv-flows-table-container" className="m-0 p-0">
      
        <TestableTable dataTestId="bv-flows-table" style={{ tableLayout: "fixed" }}>

          <TestableTableHead dataTestId="bv-flows-table-head" className="grey-title-border">
            <TestableTableRow dataTestId="bv-flows-table-head-row">
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-flow" className="w-[30%] whitespace-normal px-3 uppercase">Flow</TestableTableHeaderCell>
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-risk" className="w-[12%] whitespace-normal px-3 uppercase">Risk</TestableTableHeaderCell>
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-categories" className="hidden w-[26%] whitespace-normal px-3 uppercase md:table-cell">
                Risk Categories
              </TestableTableHeaderCell>
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-cost-at-risk" className="w-[14%] whitespace-normal px-3 uppercase text-center">Cost At Risk</TestableTableHeaderCell>
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-failed-tests" className="w-[12%] whitespace-normal px-3 uppercase text-center">Failed / Tests</TestableTableHeaderCell>
              {/* The link column has no heading, but still needs a width declaring for the fixed layout. */}
              <TestableTableHeaderCell dataTestId="bv-flows-table-head-cell-details" className="w-[6%] whitespace-normal px-3">
                <span className="sr-only">Details</span>
              </TestableTableHeaderCell>
            </TestableTableRow>
          </TestableTableHead>

          <TestableTableBody dataTestId="bv-flows-table-body">
            {visibleFlows.map((flow) => (
							
              <TestableTableRow
                key={flow.id}
                dataTestId={`bv-flow-row-${flow.id}`}
                /*
                  grey-title-border is !important because Tremor's TableBody carries
                  `divide-y divide-tremor-border`, whose `.divide-y > * ~ *` selector outranks a
                  plain utility class and would otherwise colour every separator itself.
                */
                className="last:border-0 grey-title-border"
              >
                {/*
                  The at-risk accent lives on the first cell, not the row: grey-title-border sets
                  border-color on all four sides of the <tr>, so a left border there is forced to
                  its grey. border-color does not inherit into a <td>, and divide-y only targets
                  the body's rows, so on the cell the red is uncontested.

                  text-blue-500 sits on the link rather than the cell for the same reason: index.css
                  gives that class `border-color: var(--brand-color) !important`, which would repaint
                  this border navy. On the link, where it only ever colours text, it is harmless.
                */}
                <TestableTableCell
                  dataTestId={`bv-flow-cell-${flow.id}`}
                  className={`align-top whitespace-normal break-words px-3 font-medium ${
                    flow.status === 'at_risk' ? 'border-l-2 border-l-red-500' : ''
                  }`}
                >
                  <Link data-testid={`bv-flow-link-${flow.id}`} to={buildFlowDetailPath(projectId, flow.id)} className="hover:cursor-pointer text-blue-500">
                    <TestableText dataTestId={`bv-flow-name-${flow.id}`} variant="paragraph">{flow.name}</TestableText>
                    <TestableText dataTestId={`bv-flow-description-${flow.id}`} variant="small" className="text-gray-600">{flow.description}</TestableText>
                    {flow.calculationStatus !== 'complete' && (
											<StyledTooltip content="Not every linked test returned a usable result. The flow's status is calculated from what did return, counting anything without a clear pass as non-passing, so the real position may be better than shown." placement="top">
												<TestableText dataTestId={`bv-flow-calculation-status-${flow.id}`} variant="small" className="text-orange-500 flex items-center gap-2 mt-2">
													<LuTriangleAlert size={20} color="orange" /> {flow.calculationMessage || 'This flow could not be fully calculated.'}
												</TestableText>
											</StyledTooltip>
										)}
                  </Link>
                </TestableTableCell>
                <TestableTableCell dataTestId={`bv-flow-cell-risk-${flow.id}`} className="align-top whitespace-normal px-3">
                  <RagBadge status={flow.status} testId={`bv-flow-rag-${flow.id}`} />
                </TestableTableCell>
                <TestableTableCell dataTestId={`bv-flow-cell-categories-${flow.id}`} className="hidden align-top whitespace-normal px-3 md:table-cell">
                  <div className="flex flex-wrap gap-1.5">
                    {flow.categories.map((category) => (
                      <span
                        key={category}
                        className="inline-flex items-center rounded-md border border-gray-400 px-2 py-0.5 text-xs text-gray-700 dark:border-gray-600 dark:text-gray-400"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </TestableTableCell>
                <TestableTableCell dataTestId={`bv-flow-cell-cost-at-risk-${flow.id}`} className="align-top whitespace-normal break-words px-3 text-center font-medium text-blue-500">
                  {formatCurrency(flow.costAtRisk)}
                </TestableTableCell>
                <TestableTableCell dataTestId={`bv-flow-cell-failed-tests-${flow.id}`} className="align-top whitespace-normal px-3 text-center">
                  {/* Null counts mean the flow could not be calculated — not a clean run. */}
                  {flow.totalTests == null ? (
                    <span className={COUNT_CLASS.muted} title="No result available for this flow">
                      &mdash;
                    </span>
                  ) : (
                    <>
                      {/* Both counts come from the same calculation, so a known total means a known failed count. */}
                      <span className={!!flow.failedTests && flow.failedTests > 0 ? COUNT_CLASS.failed : COUNT_CLASS.clean}>
                        {flow.failedTests ?? 0}
                      </span>
                      <span className={COUNT_CLASS.muted}> / {flow.totalTests ?? 0}</span>
                    </>
                  )}
                </TestableTableCell>
                <TestableTableCell dataTestId={`bv-flow-cell-details-${flow.id}`} className="align-top whitespace-normal px-3 text-right">
                  <Link to={buildFlowDetailPath(projectId, flow.id)} aria-label={`View details for ${flow.name}`} className="inline-flex">
                    <LuExternalLink className="h-4 w-4 text-gray-500 hover:text-blue-500" />
                  </Link>
                </TestableTableCell>
              </TestableTableRow>
            ))}
            {visibleFlows.length === 0 && (
              <TestableTableRow dataTestId="bv-flows-table-empty-row" className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                <TestableTableCell dataTestId="bv-flows-table-empty-cell" colSpan={6} className="whitespace-normal px-3 text-center text-gray-500">
                  {/*
                    Naming the filters matters when they are the reason nothing is
                    showing: the reader may have set them earlier, and the table
                    emptying after a search would otherwise look like the search's doing.
                  */}
                  {countSelected(selection) > 0
                    ? "No flows match your search and filters."
                    : "No flows match your search."}
                </TestableTableCell>
              </TestableTableRow>
            )}
          </TestableTableBody>
        </TestableTable>      
    </TestableCard>
		</>
  );
};

BusinessViewFlowsTable.propTypes = {
  flows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOf(["at_risk", "monitor", "healthy", "unavailable"]).isRequired,
      categories: PropTypes.arrayOf(PropTypes.string).isRequired,
      costAtRisk: PropTypes.number.isRequired,
      // Null when the flow could not be calculated.
      failedTests: PropTypes.number,
      totalTests: PropTypes.number,
    })
  ),
  projectId: PropTypes.string,
};

export default BusinessViewFlowsTable;
